import * as React from 'react'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields, validateFields } from '@halo/_utils/antd'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { FormComponentProps } from '@ant-design/compatible/es/form'
import { Form, Icon } from '@ant-design/compatible'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'

interface IDomainPageAnalysisConfigEditor extends FormComponentProps {
    domain: DomainDto
    pageAnalysisConfigs: IPageAnalysisConfig[]
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainPageAnalysisConfigEditorState {}

function getDomainPageAnalysisConfigName(domain: DomainDto, configs: IPageAnalysisConfig[]) {
    const configId = domain?.page_analysis_config_id
    let name = 'Default'

    if (configId) {
        const config = configs.find((pac) => pac.id === configId)
        if (config) {
            name = config.name
        }
    }

    return name
}

export const DomainPageAnalysisConfigEditor = Form.create<IDomainPageAnalysisConfigEditor>()(
    class extends React.Component<IDomainPageAnalysisConfigEditor, IDomainPageAnalysisConfigEditorState> {
        public state: IDomainPageAnalysisConfigEditorState = {}

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IDomainPageAnalysisConfigEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const currentValues = this.props.form.getFieldsValue()

            return (
                <Drawer
                    getContainer={this.appService.getAppContainer}
                    className={classnames('ddf-ecomm-settings-editor')}
                    title="Update Page Analysis Config Settings"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form>
                        <Well mode="ghost" hideHeader={true} hideFooter={true}>
                            <Form.Item className="click-through-window" label="Page Analysis Config">
                                {this.props.form.getFieldDecorator('pageAnalysisConfig', {
                                    initialValue: {
                                        key: this.props.domain.page_analysis_config_id,
                                        label: getDomainPageAnalysisConfigName(
                                            this.domain,
                                            this.props.pageAnalysisConfigs,
                                        ),
                                    },
                                })(
                                    <Select labelInValue={true}>
                                        <Select.Option key={null} value={null}>
                                            Default
                                        </Select.Option>

                                        {this.props.pageAnalysisConfigs
                                            .filter((pac) => !pac.is_default)
                                            .map((d) => {
                                                return (
                                                    <Select.Option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </Select.Option>
                                                )
                                            })}
                                    </Select>,
                                )}
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get domain(): DomainDto {
            return this.props.domain
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const values = this.props.form.getFieldsValue()
                const pageAnalysisConfigId = JSON.parse(values.pageAnalysisConfig.key || 'null')

                const { ok, data } = await this.domainService.patch(
                    this.domain.id,
                    {
                        pageAnalysisConfigId,
                    },
                    {
                        showLoadingScreen: true,
                    },
                )

                if (ok) {
                    this.props.onDomainUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IDomainPageAnalysisConfigWellProps {
    loading?: boolean
    domain: DomainDto
    pageAnalysisConfigs: IPageAnalysisConfig[]
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainPageAnalysisConfigWellState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainPageAnalysisConfigWell extends React.Component<
    IDomainPageAnalysisConfigWellProps,
    IDomainPageAnalysisConfigWellState
> {
    public state: IDomainPageAnalysisConfigWellState = {
        showDrawer: false,
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Page Analysis Config Settings"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <Icon type="edit" />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Well mode="ghost" hideHeader={true} hideFooter={true}>
                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Page Analysis Config</div>
                                    {getDomainPageAnalysisConfigName(this.domain, this.props.pageAnalysisConfigs)}
                                </div>
                            </Well>

                            <DomainPageAnalysisConfigEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                pageAnalysisConfigs={this.props.pageAnalysisConfigs}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
